import {
  Table,
  Button,
  Card,
  Row,
  Col,
  Statistic,
  Popover,
  Descriptions,
  Skeleton,
  Space,
} from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { useFetchAccountsQuery } from "features/accountsTable/agencyPortal/accountsTableAPI";
import { useFetchDebtorsQuery } from "features/debtorProfile/debtorProfileAPI";
import { useFetchCollectorsQuery } from "features/home/agencyPortal/homeAPI";
import { useMemo } from "react";
import { formatCurrency, formatDate, insertIf } from "common/utils";
import styled from "styled-components";
import currency from "currency.js";
import moment from "moment-timezone";

const StyledInfoTitle = styled.div`
  color: grey;
  font-size: 12px;
`;

const StyledInfoValue = styled.div`
  margin-left: 4px;
  font-size: 12px;
`;

const StyledQuestionCircleOutlined = styled(QuestionCircleOutlined)`
  color: #1677ff;
  margin-left: 4px;
`;

export default function PacketedAccountsTable({ packetId }) {
  const { data: packetedAccounts, isLoading } = useFetchAccountsQuery(
    {
      filters: {
        debtorPacketIds: [packetId],
      },
    },
    {
      skip: !packetId,
    },
  );
  const { data: packetedDebtors } = useFetchDebtorsQuery(
    {
      filters: {
        debtorPacketIds: [packetId],
      },
    },
    {
      skip: !packetId,
    },
  );
  const { data: collectors } = useFetchCollectorsQuery();

  // Calculate packet totals
  const packetTotals = useMemo(() => {
    if (!packetedAccounts || !packetedDebtors)
      return { totalBalance: 0, totalLawsuits: 0, totalLawsuitsBalance: 0 };

    const totalBalance = formatCurrency(
      packetedAccounts.results?.reduce(
        (sum, account) => sum.add(account.totalBalance),
        currency(0),
      ) || currency(0),
    );

    const uniqueLawsuitIds = new Set();
    packetedDebtors.results?.forEach((debtor) => {
      debtor.lawsuitIds?.forEach((lawsuitId) => {
        uniqueLawsuitIds.add(lawsuitId);
      });
    });
    const totalLawsuits = uniqueLawsuitIds.size;
    const totalLawsuitsBalance = formatCurrency(
      packetedDebtors.results?.reduce(
        (sum, debtor) => sum.add(debtor.accountsCurrentTotalLegalBalance),
        currency(0),
      ) || currency(0),
    );

    return { totalBalance, totalLawsuits, totalLawsuitsBalance };
  }, [packetedAccounts, packetedDebtors]);

  const handleDebtorClick = (debtorId) => {
    window.open(`/debtors/${debtorId}`, "_blank");
  };

  const accountColumns = [
    {
      title: "Identifiers",
      key: "externalAccountId",
      render: (text, record) => (
        <Col>
          <Row align="middle">
            <StyledInfoTitle>External ID:</StyledInfoTitle>
            <StyledInfoValue>{record?.externalId ?? "-"}</StyledInfoValue>
          </Row>
          <Row align="middle">
            <StyledInfoTitle>Client Ref ID:</StyledInfoTitle>
            <StyledInfoValue>{record?.clientReferenceId ?? "-"}</StyledInfoValue>
          </Row>
        </Col>
      ),
    },
    {
      title: "Client",
      key: "creditorId",
      render: (text, record) => `${record.currentCreditorName} (ID: ${record.creditorId})`,
    },
    {
      title: "Debtor Name(s)",
      key: "debtorNames",
      render: (text, record) => {
        const debtorIds = record.debtors.map((debtor) => debtor.id) || [];
        return (
          <div>
            {debtorIds.map((debtorId) => {
              const debtor = packetedDebtors?.results?.find((d) => d.id === debtorId);
              return (
                <div key={debtorId}>
                  <Button type="link" onClick={() => handleDebtorClick(debtorId)}>
                    {debtor?.name || `Debtor ${debtorId}`}
                  </Button>
                </div>
              );
            })}
          </div>
        );
      },
    },
    {
      title: "Balance",
      children: [
        {
          title: "Original",
          width: 125,
          dataIndex: "originalTotal",
          render: (text, record) => {
            const balanceItems = [
              {
                key: "originalPrincipal",
                label: "Principal",
                value: formatCurrency(record.originalPrincipal),
              },
              {
                key: "originalInterest",
                label: `Agency Interest (${
                  currency(record?.interestRate, { precision: 4 }).multiply(100).value
                }%)`,
                value: formatCurrency(record.originalInterest),
              },
              {
                key: "originalClientInterest",
                label: "Client Interest",
                value: formatCurrency(record.originalClientInterest),
              },
              ...insertIf(record.legalStatus === "judgment", {
                key: "originalPrejudgmentInterest",
                label: "Prejudgment Interest",
                value: formatCurrency(record.originalPrejudgmentInterest),
              }),
              {
                key: "originalTotalFeesDue",
                label: "Fees",
                value: formatCurrency(record.originalTotalFeesDue),
              },
              {
                key: "originalTotal",
                label: <strong>Total</strong>,
                value: <strong>{formatCurrency(record.originalTotal)}</strong>,
              },
            ];
            const totalBalanceContent = (
              <Col>
                <Descriptions bordered column={1} size="small">
                  {balanceItems.map((item) => (
                    <Descriptions.Item key={item.key} label={item.label}>
                      {item.value}
                    </Descriptions.Item>
                  ))}
                </Descriptions>
              </Col>
            );
            return (
              <Row align="middle">
                {formatCurrency(text)}
                <Popover
                  content={totalBalanceContent}
                  title="Original Balance Breakdown"
                  trigger="hover"
                  placement="right"
                >
                  <StyledQuestionCircleOutlined />
                </Popover>
              </Row>
            );
          },
        },
        {
          title: "Turnover",
          width: 125,
          dataIndex: "turnoverTotalBalance",
          render: (text) => formatCurrency(text),
        },
        {
          title: "Current",
          width: 125,
          dataIndex: "totalBalance",
          render: (text, record) => {
            const balanceItems = [
              {
                key: "principal",
                label: "Principal",
                value: formatCurrency(record.principalBalance),
              },
              {
                key: "interest",
                label: `Agency Interest (${
                  currency(record?.interestRate, { precision: 4 }).multiply(100).value
                }%)`,
                value: formatCurrency(record.interestBalance),
              },
              {
                key: "clientInterest",
                label: "Client Interest",
                value: formatCurrency(record.clientInterestBalance),
              },
              ...insertIf(record.legalStatus === "judgment", {
                key: "prejudgmentInterest",
                label: "Prejudgment Interest",
                value: formatCurrency(record.prejudgmentInterestBalance),
              }),
              {
                key: "totalFees",
                label: "Fees",
                value: formatCurrency(record.totalFeesBalance),
              },
              {
                key: "total",
                label: <strong>Total</strong>,
                value: <strong>{formatCurrency(text)}</strong>,
              },
            ];
            const totalBalanceContent = (
              <Col>
                <Descriptions bordered column={1} size="small">
                  {balanceItems.map((item) => (
                    <Descriptions.Item key={item.key} label={item.label}>
                      {item.value}
                    </Descriptions.Item>
                  ))}
                </Descriptions>
              </Col>
            );
            return (
              <Row align="middle">
                {formatCurrency(text)}
                <Popover
                  content={totalBalanceContent}
                  title="Current Balance Breakdown"
                  trigger="hover"
                  placement="right"
                >
                  <StyledQuestionCircleOutlined />
                </Popover>
              </Row>
            );
          },
        },
      ],
    },
    {
      title: "Key Dates",
      key: "keyDates",
      render: (text, record) => (
        <Col>
          <Row align="middle">
            <StyledInfoTitle>Service Date:</StyledInfoTitle>
            <StyledInfoValue>{formatDate(record?.dateOfService) ?? "-"}</StyledInfoValue>
          </Row>
          <Row align="middle">
            <StyledInfoTitle>Turnover Date:</StyledInfoTitle>
            <StyledInfoValue>{formatDate(record?.turnoverDate) ?? "-"}</StyledInfoValue>
          </Row>
          <Row align="middle">
            <StyledInfoTitle>Open Date:</StyledInfoTitle>
            <StyledInfoValue>{formatDate(record?.openDate) ?? "-"}</StyledInfoValue>
          </Row>
          <Row align="middle">
            <StyledInfoTitle>Itemization Date:</StyledInfoTitle>
            <StyledInfoValue>{formatDate(record?.itemizationDate) ?? "-"}</StyledInfoValue>
          </Row>
          <Row align="middle">
            <StyledInfoTitle>Last Payment Date:</StyledInfoTitle>
            <StyledInfoValue>{formatDate(record?.lastPaymentDate) ?? "-"}</StyledInfoValue>
          </Row>
          <Popover
            content={
              <>
                <Row align="middle">
                  <StyledInfoTitle>Service Date:</StyledInfoTitle>
                  <StyledInfoValue>{formatDate(record?.dateOfService) ?? "-"}</StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Turnover Date:</StyledInfoTitle>
                  <StyledInfoValue>{formatDate(record?.turnoverDate) ?? "-"}</StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Open Date:</StyledInfoTitle>
                  <StyledInfoValue>{formatDate(record?.openDate) ?? "-"}</StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Upload Date:</StyledInfoTitle>
                  <StyledInfoValue>{formatDate(record?.uploadDate) ?? "-"}</StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Itemization Date:</StyledInfoTitle>
                  <StyledInfoValue>{formatDate(record?.itemizationDate) ?? "-"}</StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Date of First Delinquency:</StyledInfoTitle>
                  <StyledInfoValue>
                    {formatDate(record?.dateOfFirstDelinquency) ?? "-"}
                  </StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Charge-off Date:</StyledInfoTitle>
                  <StyledInfoValue>{formatDate(record?.chargeOffDate) ?? "-"}</StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Interest Calculation Date:</StyledInfoTitle>
                  <StyledInfoValue>{formatDate(record?.interestStartDate) ?? "-"}</StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Out Of Statute Date:</StyledInfoTitle>
                  <StyledInfoValue>
                    {formatDate(record?.statuteOfLimitationsExpiryDate) ?? "-"}
                  </StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Last Payment Date:</StyledInfoTitle>
                  <StyledInfoValue>{formatDate(record?.lastPaymentDate) ?? "-"}</StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Judgment Date:</StyledInfoTitle>
                  <StyledInfoValue>{formatDate(record?.judgmentDate) ?? "-"}</StyledInfoValue>
                </Row>
              </>
            }
            title="Account Dates"
            trigger="click"
          >
            <Button type="link">See All</Button>
          </Popover>
        </Col>
      ),
    },
    {
      title: "Status Code",
      dataIndex: "statusCode",
      key: "statusCode",
      render: (text) => text || "-",
    },
    {
      title: "Collector",
      dataIndex: "assigneeId",
      key: "assigneeId",
      render: (text) => {
        const collector = collectors?.find((c) => c.id === text);
        return collector?.name || "-";
      },
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      {/* Account Summary */}
      {isLoading ? (
        <Skeleton loading={isLoading} active />
      ) : (
        <Card>
          <Space direction="horizontal" align="center" size="large">
            <Statistic title="Total Accounts" value={packetedAccounts?.results?.length || 0} />
            <Statistic title="Total Balance" value={packetTotals.totalBalance} />
            <Statistic title="Total Lawsuits" value={packetTotals.totalLawsuits} />
            <Statistic title="Total Lawsuit Balance" value={packetTotals.totalLawsuitsBalance} />
          </Space>
        </Card>
      )}
      {/* Accounts Table */}
      <Table
        loading={isLoading}
        columns={accountColumns}
        dataSource={
          packetedAccounts?.results
            ? [...packetedAccounts.results].sort((a, b) => {
                if (!a.turnoverDate) return 1;
                if (!b.turnoverDate) return -1;
                return moment(a.turnoverDate).diff(moment(b.turnoverDate));
              })
            : []
        }
        rowKey="id"
        bordered
        scroll={{ x: "max-content" }}
        pagination={{ defaultPageSize: 100 }}
      />
    </Space>
  );
}
